import {GUID, VivoPortal} from "../../../../utils/AppConstants";
import {useEffect, useState} from "react";
import {ApiResponseDto, BasePopupProps, ComponentDataModel, NameWithId} from "../../../../utils/Models";
import {useTranslation} from "react-i18next";
import axios from "axios";
import {ApiUrl_Components_Get, ApiUrl_Components_Save} from "../../../../utils/Endpoints";
import {useFlintNotificationContext} from "../../common/notifications/FlintNotificationContext";
import {useAuthenticationContext} from "../../../../utils/auth/AuthenticationContext";
import {ComponentDetailsModel} from "./inkseries.models";
import FlintDropDownList from "../../../controls/FlintDropDownList";
import FlintTextArea from "../../../controls/FlintTextArea";
import EnumSegment = VivoPortal.EnumSegment;
import FlintTextBox from "../../../controls/FlintTextBox";
import useVivoValidation from "../../../../utils/validation/useVivoValidation";
import * as Yup from "yup";
import {WindowActionsBar} from "@progress/kendo-react-dialogs";
import FlintButton from "../../../controls/FlintButton";

export default function InkComponentEditComponent(props: InkComponentEditComponentProps) {
    const {t} = useTranslation();
    const {state, handlers, validation} = useInkComponentEditComponent(props);

    return (
        <div className={"form"}>
            <div className="form-group">
                <FlintTextBox className={"form-control"} label={t("lblColorCode")}
                              disabled={state.isLoading.get}
                              value={state.model.get?.ComponentData.Name}
                              maxLength={9}
                              validationData={{
                                  validationMessage: validation.formErrors?.Name,
                                  validateFunction: validation.validateForm,
                                  optionalObjectToValidate: state.model.get?.ComponentData
                              }}
                              onChange={e => state.model.set('Name', e.value)}
                />
            </div>
            {state.resinTypeVisible.get &&
                <div className={"row"}>
                    <div className="form-group col-md-6">
                        <FlintDropDownList<NameWithId> className={"form-control"} label={t("lblSelectResinType")}
                                                       dataSource={state.model.get?.ResinTypeList}
                                                       disabled={state.isLoading.get}
                                                       validationData={{
                                                           validationMessage: validation.formErrors?.ResinType,
                                                           validateFunction: validation.validateForm,
                                                           optionalObjectToValidate: state.model.get?.ComponentData
                                                       }}
                                                       selectedValue={state.selectedResinType.get}
                                                       onItemSelected={handlers.handleResinTypeChange}
                        />
                    </div>
                    <div className="form-group col-md-6">
                        <FlintDropDownList<NameWithId> className={"form-control"}
                                                       label={t("lblSelectResinTypeGroup")}
                                                       dataSource={state.model.get?.ResinTypeGroupList}
                                                       disabled={state.isLoading.get}
                                                       validationData={{
                                                           validationMessage: validation.formErrors?.ResinTypeGroup,
                                                           validateFunction: validation.validateForm,
                                                           optionalObjectToValidate: state.model.get?.ComponentData
                                                       }}
                                                       selectedValue={state.selectedResinTypeGroup.get}
                                                       onItemSelected={handlers.handleResinTypeGroupChange}
                        />
                    </div>
                </div>}
            <div className="form-group mb-3">
                <FlintTextArea className={"form-control"}
                               label={t("lblDescription")}
                               value={state.model.get?.ComponentData.Description}
                               disabled={state.isLoading.get}
                               onChange={e => state.model.set('Description', e.value)}/>
            </div>
            <WindowActionsBar layout={"center"}>
                <FlintButton disabled={state.isLoading.get} themeColor={"secondary"}
                             onClick={props.OnCancelled}>{t("Cancel")}</FlintButton>
                <FlintButton disabled={state.isLoading.get || !state.selectedLab.get} themeColor={"primary"}
                             onClick={handlers.handleSave}>{t("Save")}</FlintButton>
            </WindowActionsBar>

        </div>
    )
}

export interface InkComponentEditComponentProps extends BasePopupProps {
    componentGuid?: GUID;
}

function useInkComponentEditComponent(props: InkComponentEditComponentProps) {
    const {t} = useTranslation();
    const [resinTypeVisible, setResinTypeVisible] = useState(false);
    const [model, setModel] = useState<ComponentDetailsModel>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const {showNotification} = useFlintNotificationContext();
    const [selectedResinType, setSelectedResinType] = useState<NameWithId>();
    const [selectedResinTypeGroup, setSelectedResinTypeGroup] = useState<NameWithId>();
    const {rootModel} = useAuthenticationContext();
    const validateSchema = Yup.object().shape({
        Name: Yup.string().trim()
            .required(t('COMPONENT_VALIDATION_CodeRequired'))
            .max(9, t('COMPONENT_VALIDATION_CodeMaxLength')),

        ResinType: Yup.string().when([], () => {
            return resinTypeVisible
                ? Yup.string().required('Resin type is required')
                : Yup.string().nullable(); // No validation if the condition is false
        }),
        ResinTypeGroup: Yup.string().when([], () => {
            return resinTypeVisible
                ? Yup.string().required('Resin type group is required')
                : Yup.string().nullable(); // No validation if the condition is false
        }),
    });
    const {formErrors, validateForm} = useVivoValidation(validateSchema)

    useEffect(() => {
        validateForm(model?.ComponentData)
            .catch(() => {
            });
    }, [model?.ComponentData]);

    useEffect(() => {
        setResinTypeVisible(rootModel?.CurrentUserProfile.DivisionId == EnumSegment.Flexible_Packaging);
        axios.get<ApiResponseDto<ComponentDetailsModel>>(ApiUrl_Components_Get,
            {params: {guid: props.componentGuid}}).then((response) => {
            let newModel = response.data.Result;
            if (newModel.ComponentData.Id == 0) {
                newModel.ComponentData.Name = "";
                newModel.ComponentData.Description = "";
            }
            setModel(newModel);
        }).catch((e) => {
            showNotification(e);
        }).finally(() => {
            setIsLoading(false)
        });
    }, [])

    useEffect(() => {
        if (resinTypeVisible) {
            let resinType = model?.ResinTypeList.filter(x => {
                return x.IdString == model?.ComponentData.ResinType;
            })[0];
            setSelectedResinType(resinType);
            let resinTypeGroup = model?.ResinTypeGroupList.filter(x => {
                return x.IdString == model?.ComponentData.ResinTypeGroup;
            })[0];
            setSelectedResinTypeGroup(resinTypeGroup);
        } else {
            setSelectedResinType(undefined);
            setSelectedResinTypeGroup(undefined);
        }
    }, [model])

    function updateModel(key: string, newValue: any) {
        setModel(prev => prev == null ? undefined : ({
            ...prev,
            ComponentData: {
                ...prev!.ComponentData,
                [key]: newValue,
            },
        }));
    }

    function handleResinTypeChange(e: NameWithId) {

        setModel(prev => prev == null ? undefined : ({
            ...prev,
            ComponentData: {
                ...prev!.ComponentData,
                ResinType: e.IdString,
            },
        }));
    }

    function handleResinTypeGroupChange(e: NameWithId) {
        setModel(prev => prev == null ? undefined : ({
            ...prev,
            ComponentData: {
                ...prev!.ComponentData,
                ResinTypeGroup: e.IdString,
            },
        }));
    }

    function saveComponent() {
        setIsLoading(true);
        return axios.post<ApiResponseDto<ComponentDataModel>>(ApiUrl_Components_Save, model?.ComponentData)
            .then(() => {

            })
            .catch(error => {
                showNotification(error)
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    function handleSave() {
        validateForm(model?.ComponentData)
            .then(() => {
                saveComponent().then(() => {
                    props.OnConfirmedAndSaved();
                })
                    .catch(showNotification);
            })
            .catch(() => {
            });
    }

    return {
        state: {
            model: {get: model, set: updateModel},
            isLoading: {get: isLoading},
            resinTypeVisible: {get: resinTypeVisible},
            selectedResinType: {get: selectedResinType},
            selectedResinTypeGroup: {get: selectedResinTypeGroup},
            selectedLab: {get: rootModel?.LabData?.CurrentLab}
        },
        handlers: {
            handleResinTypeChange, handleResinTypeGroupChange, handleSave
        },
        validation: {formErrors, validateForm}
    }
}